/**
 * OpenExternalPortal (Story #5351 W1-T17, Screen Contract G09: "Open
 * external portal"). Nav position: "Portal/environment context action or
 * approved shell/footer shortcut" — mounted in Sidebar's footer, replacing
 * the single hard-coded CloudFront link that sat there before this task
 * (the exact gap this contract row names: "The baseline footer uses a
 * hard-coded CloudFront URL. The target resolves one environment-specific,
 * allow-listed registry binding with evidence.").
 *
 * Honest scope: there is no live portal-registry API anywhere in
 * services/api.ts, and `scope.portalId` (ScopeContext's canonical URL key)
 * has no established vocabulary yet — nothing in the app writes it today
 * (ModelConfigPortalContext's OWNING_PORTALS is a deliberately separate
 * `?portal=` concept, per that file's own doc comment about the id
 * mismatch with pages/Portals.tsx's `poolKey`). So rather than invent a
 * live, per-environment registry that does not exist, this component ships
 * a small static allow-list (the same known portal→URL bindings already
 * hard-coded in pages/Portals.tsx's PORTALS array — duplicated here rather
 * than importing that module-local, unexported const; a follow-up could
 * export it once as the shared source) and is honest about the rest: no
 * `environmentId` binding exists per portal (every entry is a single fixed
 * host), so selecting an environment other than "the one bound host" —
 * or having no portalId in scope at all, the common case today — renders
 * the mockup's own "External binding unresolved" state rather than a
 * fabricated per-environment URL.
 *
 * Safety (contract's Primary control): opens via window.open with
 * `noopener,noreferrer` (no `window.opener` handed to the destination, no
 * Referer leak of the Management URL) and passes no token/credential of
 * any kind — this component makes no API call and reads no auth storage.
 */
import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useScope } from '../../contexts/ScopeContext';
import UnavailableReason from '../ui/UnavailableReason';

interface AllowListEntry {
  portalId: string;
  label: string;
  url: string;
}

/** Mirrors pages/Portals.tsx's PORTALS ids/urls for the portals with a known, live, allow-listed host. */
const ALLOW_LIST: AllowListEntry[] = [
  { portalId: 'stakeholder-portal', label: 'Stakeholder Portal', url: 'https://d2k5ne4hoaiwmb.cloudfront.net' },
  { portalId: 'innovateflow-portal', label: 'InnovateFlow', url: 'https://d3h01kp97t351k.cloudfront.net' },
];

function resolveBinding(portalId: string | null): AllowListEntry | undefined {
  if (!portalId) return undefined;
  return ALLOW_LIST.find((e) => e.portalId === portalId);
}

const OpenExternalPortal: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const scope = useScope();
  const [confirming, setConfirming] = useState(false);
  const binding = resolveBinding(scope.portalId) ?? ALLOW_LIST[0]; // default: the one always-known stakeholder binding

  const openPortal = () => {
    // Safe new-window navigation: no opener handed to the destination, no
    // Referer leak, and this function transmits nothing from localStorage
    // or apiClient — there is no credential anywhere in this component.
    window.open(binding.url, '_blank', 'noopener,noreferrer');
    setConfirming(false);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        title="Open external portal"
        aria-label="Open external portal"
        className="w-full flex items-center justify-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
      >
        <ExternalLink className="w-5 h-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="w-full flex items-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors text-sm"
      >
        <ExternalLink className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span className="truncate ml-3">Open external portal</span>
      </button>
      {confirming && (
        <div role="dialog" aria-label="Open external portal" className="mt-2 mx-3 p-3 rounded-lg bg-white text-gray-900 text-xs space-y-2">
          <p className="font-semibold">{binding.label}</p>
          <p className="text-gray-600">
            Leaving Management: opens {new URL(binding.url).host} in a new tab. No Management credential is transmitted.
          </p>
          {!scope.portalId && (
            <UnavailableReason
              className="text-[11px]"
              reason="No portal is in scope — opening the default known binding. Per-environment registry resolution is not implemented yet."
            />
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="flex-1 px-2 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={openPortal}
              className="flex-1 px-2 py-1.5 rounded bg-iflow-700 text-white hover:bg-iflow-800"
            >
              Open external portal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenExternalPortal;
